import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';
import { STADIUM_BG } from '@/config/assets';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API_URL}/api/user/verify-reset-token/${token}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/user/reset-password`, {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${STADIUM_BG})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/30 shadow-xl mx-auto">
              <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
            </div>
          </Link>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 pb-20">
          <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl border-0 rounded-2xl overflow-hidden">
            {verifying ? (
              <CardContent className="py-12">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 border-3 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
                  <p className="text-slate-500 mt-4">Verifying reset link...</p>
                </div>
              </CardContent>
            ) : !token || !tokenValid ? (
              <>
                <CardHeader className="text-center pb-2 pt-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-800">Invalid Link</CardTitle>
                  <p className="text-slate-500 text-sm mt-2">
                    This password reset link is invalid or has expired.
                  </p>
                </CardHeader>
                <CardContent className="pb-6">
                  <div className="space-y-3">
                    <Link to="/forgot-password">
                      <Button className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC]">
                        Request New Reset Link
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="ghost" className="w-full text-slate-600">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </>
            ) : success ? (
              <>
                <CardHeader className="text-center pb-2 pt-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-800">Password Reset!</CardTitle>
                  <p className="text-slate-500 text-sm mt-2">
                    Your password has been successfully reset. You can now login with your new password.
                  </p>
                </CardHeader>
                <CardContent className="pb-6">
                  <Link to="/">
                    <Button className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC]">
                      Go to Login
                    </Button>
                  </Link>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="text-center pb-2 pt-6">
                  <CardTitle className="text-2xl font-bold text-slate-800">Reset Password</CardTitle>
                  <p className="text-slate-500 text-sm mt-2">
                    Enter your new password below
                  </p>
                </CardHeader>

                <CardContent className="pb-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1.5 block text-slate-700">
                        New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="At least 6 characters"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          required
                          data-testid="reset-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block text-slate-700">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="password"
                          placeholder="Confirm your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          required
                          data-testid="reset-confirm-password"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                      disabled={loading}
                      data-testid="reset-submit"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </form>

                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <Link 
                      to="/" 
                      className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-[#0055FF] transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Link>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-white/50 text-sm">
            © 2026 Gam Ticket. The Premier Ticketing Platform for The Gambia.
          </p>
        </footer>
      </div>
    </div>
  );
}
